/**
 * F-90 script import RUNNER: parsed rows → the Original Script column, without
 * freezing the tab and as one undo step.
 *
 * The shape of this file is dictated by F-90's two halves:
 *
 *  - **"without freezing the tab"** — the rows are written in bounded ~500-row
 *    transactions (`chunk-plan.ts`) with a real yield to the event loop between
 *    them, so the browser paints the progress bar and stays responsive. Each
 *    chunk uses the BULK `addRows` accessor: `addRow` in a loop is O(n²)
 *    (measured 93 ms at 2k, ~1 min at the 50k ceiling) and is the exact freeze
 *    the requirement forbids.
 *  - **"one undo step"** — a Yjs transaction is synchronous, so the chunks
 *    cannot be one transaction; `session.beginUndoGroup()` merges them into one
 *    undo-stack item instead. Ctrl+Z after an import removes the import, not
 *    its last 500 rows.
 *
 * Everything goes through `BoardSession` (shared accessors, LOCAL_ORIGIN) —
 * this module never touches Yjs.
 */
import type { BoardSession } from "../../core/session.js";
import { IMPORT_CHUNK_ROWS, planChunks, reusableTrailingRows } from "./chunk-plan.js";

export interface ImportScriptOptions {
  session: BoardSession;
  sheetId: string;
  /** The target column — always the F-28 "Original Script" column. */
  colId: string;
  /** Values to write, in file order (`ImportPreview.rows`). */
  rows: readonly string[];
  /** Called after every chunk with rows written so far. */
  onProgress?: (done: number, total: number) => void;
  /** Rows per transaction — only a test has a reason to change this. */
  chunkSize?: number;
  /** Yield between chunks. Injectable so tests need no real timers. */
  yieldToUi?: () => Promise<void>;
}

export interface ImportScriptResult {
  /** Rows written. */
  written: number;
  /** Existing trailing EMPTY rows filled in place (F-90 note in chunk-plan). */
  reused: number;
  /** Rows appended to the sheet. `reused + appended === written`. */
  appended: number;
}

/**
 * Hand the frame back to the browser. Shared by every chunked bulk write (the
 * import here, and the F-16 large paste in GridTable).
 *
 * `setTimeout(0)` rather than a microtask: a microtask (`Promise.resolve()`,
 * `queueMicrotask`) runs BEFORE the browser can paint, so the whole import
 * would still land in one frame and the progress bar would jump from 0 to 100
 * having frozen the tab exactly as much as one giant transaction. A macrotask
 * is what actually yields.
 */
export function yieldToUi(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 0));
}

/**
 * Rows at the end of the sheet that hold nothing in any column, newest-first
 * scan, at most `limit` of them.
 *
 * Reads at most `limit` rows of a possibly-10k-row sheet: it walks backwards
 * and stops at the first row with content, which is the only region that can
 * possibly be reusable.
 */
function trailingEmptyRowIds(
  session: BoardSession,
  sheetId: string,
  limit: number,
): string[] {
  const { board } = session;
  const rowIds = board.sortedRowIds(sheetId);
  const colIds = board.sortedColIds(sheetId);
  const isEmpty = (rowId: string): boolean =>
    colIds.every((colId) => {
      const snapshot = board.getCell(sheetId, rowId, colId);
      return snapshot === undefined || snapshot.v === null || snapshot.v === "";
    });

  // Only the last `limit` rows can possibly be reusable, so only they are read.
  const tail = rowIds.slice(Math.max(0, rowIds.length - limit));
  const reusable = reusableTrailingRows(tail.map(isEmpty), limit);
  return reusable === 0 ? [] : tail.slice(tail.length - reusable);
}

/**
 * Write `rows` into the Original Script column. Resolves when every row is
 * written; the caller renders progress and the completion toast.
 *
 * ORDERING: reuse is decided and consumed in the FIRST transaction, from a read
 * taken inside it — so no concurrently-deleted row can strand a line. Every
 * later chunk only ever APPENDS, which nothing else on the board can reorder
 * (C1: appends mint fresh ord keys after the current last row).
 */
export async function runScriptImport(options: ImportScriptOptions): Promise<ImportScriptResult> {
  const { session, sheetId, colId, rows } = options;
  const total = rows.length;
  const chunkSize = options.chunkSize ?? IMPORT_CHUNK_ROWS;
  const yieldChunk = options.yieldToUi ?? yieldToUi;
  if (total === 0) return { written: 0, reused: 0, appended: 0 };

  const endGroup = session.beginUndoGroup();
  try {
    // Reuse is capped at one chunk's worth so this first transaction obeys the
    // same bound as every other, and so the common case (a fresh 3-row board)
    // costs a 3-row backwards scan rather than a full-sheet one.
    let reused = 0;
    let written = 0;
    const reuseIds = trailingEmptyRowIds(session, sheetId, Math.min(total, chunkSize));
    if (reuseIds.length > 0) {
      // F-45, same guard as the append loop below: while frozen, `session.batch`
      // never runs its callback and every mutator is a no-op. Counting these
      // rows unconditionally would report "Imported 3 lines of 10,000" for an
      // import that wrote nothing at all — the loop below then breaks on
      // `added === 0` and the toast inherits the lie. The flag is set INSIDE the
      // transaction, so it can only be true if the writes actually happened.
      let landed = false;
      session.batch(() => {
        reuseIds.forEach((rowId, i) => {
          session.setCellValue(sheetId, rowId, colId, rows[i] as string);
        });
        landed = true;
      });
      if (!landed) return { written: 0, reused: 0, appended: 0 };
      reused = reuseIds.length;
      written = reused;
      options.onProgress?.(written, total);
      await yieldChunk();
    }

    for (const chunk of planChunks(total - reused, chunkSize)) {
      const values = rows.slice(reused + chunk.start, reused + chunk.end);
      let added = 0;
      session.batch(() => {
        // One bulk insert + one write per row, in ONE transaction: one sync
        // unit, so peers see the rows and their text arrive together rather
        // than watching a block of empty rows fill in.
        const rowIds = session.insertRows(sheetId, values.length);
        rowIds.forEach((rowId, i) => {
          session.setCellValue(sheetId, rowId, colId, values[i] as string);
        });
        added = rowIds.length;
      });
      // F-45: going offline mid-import freezes every session mutator into a
      // no-op. Stop and report the honest count rather than spinning through
      // 19 more chunks that write nothing and then claiming 10,000 rows.
      if (added === 0) break;
      written += added;
      options.onProgress?.(written, total);
      await yieldChunk();
    }
    return { written, reused, appended: written - reused };
  } finally {
    endGroup();
  }
}
